/**
 * CreateFolderDialog Component
 *
 * Simple dialog for creating new folders.
 *
 * Sprint 5.2 - Create folder dialog
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';

/**
 * Props for CreateFolderDialog
 */
export interface CreateFolderDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when folder is created */
  onCreate: (name: string) => Promise<unknown>;
}

/**
 * CreateFolderDialog Component
 */
export function CreateFolderDialog({
  open,
  onClose,
  onCreate,
}: CreateFolderDialogProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
    }
  }, [open]);

  // Form validation
  const isValid = name.trim().length > 0;

  // Handle form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isValid) return;

    setLoading(true);
    setError(null);

    try {
      await onCreate(name.trim());
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create folder';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [name, isValid, onCreate, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth data-testid="onb-folder-dialog">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              label="Folder Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              data-testid="onb-folder-name-input"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            data-testid="onb-folder-create-btn"
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateFolderDialog;
